import { useState, useEffect } from 'react';
import { api } from '../../services/api';
import { formatPrice } from '../../utils/format';

interface Permission {
  id: number;
  code: string;
  nom: string;
  description: string;
}

interface Utilisateur {
  id: string;
  nom: string;
  role: string;
}

interface PermissionsModalProps {
  user: Utilisateur;
  onClose: () => void;
  onSuccess: () => void;
}

const CATEGORIES = [
  { id: 'dash', label: 'Tableaux de bord', icon: 'ri-dashboard-line', perms: ['dash_gerant', 'dash_atelier', 'dash_vendeur', 'dash_coupeur', 'dash_tailleur'] },
  { id: 'cmd', label: 'Clients, Commandes & Ventes', icon: 'ri-shopping-bag-3-line', perms: ['cmd_voir', 'cmd_creer', 'cmd_modifier', 'cmd_valider', 'cmd_affecter', 'vente_voir', 'vente_creer', 'client_voir', 'mesure_voir', 'mesure_creer', 'mesure_modifier'] },
  { id: 'stock', label: 'Stocks & Tissus', icon: 'ri-stack-line', perms: ['stock_voir', 'stock_reappro', 'stock_creer', 'stock_modifier', 'tissu_assigner', 'fourn_voir', 'fourn_gerer'] },
  { id: 'reports', label: 'Rapports & Analyses', icon: 'ri-bar-chart-box-line', perms: ['ca_voir', 'rapport_personnel', 'rapport_stocks', 'rapport_ca'] },
  { id: 'hr', label: 'RH & Paie', icon: 'ri-team-line', perms: ['paie_voir', 'paie_valider', 'user_voir', 'user_gerer'] },
  { id: 'config', label: 'Configuration', icon: 'ri-settings-4-line', perms: ['config_entreprise', 'config_mesures'] },
];

export default function PermissionsModal({ user, onClose, onSuccess }: PermissionsModalProps) {
  const [allPermissions, setAllPermissions] = useState<Permission[]>([]);
  const [userPerms, setUserPerms] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState(CATEGORIES[0].id);

  useEffect(() => {
    const fetchPerms = async () => {
      const token = localStorage.getItem('atelier_bassoum_token');
      if (!token) return;
      try {
        const [all, mine, matrix] = await Promise.all([
          api.getPermissions(token),
          api.getUserPermissions(token, user.id),
          api.getRolePermissions(token)
        ]);
        
        setAllPermissions(all);
        const currentCodes = mine.map((p: any) => p.code.toLowerCase());
        
        if (currentCodes.length === 0 && all.length > 0) {
          // Utiliser les permissions par défaut du rôle (depuis la matrice)
          // Normaliser les clés de la matrice en minuscules pour la comparaison
          const lowerMatrix: any = {};
          Object.keys(matrix).forEach(k => {
            lowerMatrix[k.toLowerCase()] = matrix[k];
          });

          const defaultCodes = all
            .filter(p => {
              const code = p.code.toLowerCase();
              return lowerMatrix[code] && lowerMatrix[code][user.role];
            })
            .map(p => p.code.toLowerCase());
            
          setUserPerms(defaultCodes);
        } else {
          setUserPerms(currentCodes);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchPerms();
  }, [user.id, user.role]);

  const handleToggle = async (code: string) => {
    const isAssigned = userPerms.includes(code);
    let newPerms: string[];
    if (isAssigned) {
      newPerms = userPerms.filter(c => c !== code);
    } else {
      newPerms = [...userPerms, code];
    }
    setUserPerms(newPerms);
  };

  const handleSelectAllCategory = () => {
    if (!currentCategory) return;
    const categoryCodes = allPermissions
      .filter(p => currentCategory.perms.includes(p.code.toLowerCase()))
      .map(p => p.code.toLowerCase());
    
    setUserPerms(prev => {
      const otherPerms = prev.filter(code => !categoryCodes.includes(code));
      return [...otherPerms, ...categoryCodes];
    });
  };

  const handleDeselectAllCategory = () => {
    if (!currentCategory) return;
    const categoryCodes = currentCategory.perms;
    setUserPerms(prev => prev.filter(code => !categoryCodes.includes(code)));
  };

  const handleSave = async () => {
    const token = localStorage.getItem('atelier_bassoum_token');
    if (!token) return;
    setSaving(true);
    try {
      const permIds = allPermissions
        .filter(p => userPerms.includes(p.code.toLowerCase()))
        .map(p => p.id);
      
      await api.updateUserPermissions(token, user.id, permIds);
      
      // Déclencher un événement global pour que le menu se mette à jour instantanément
      window.dispatchEvent(new Event('permissionsChanged'));
      
      onSuccess();
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const currentCategory = CATEGORIES.find(c => c.id === activeTab);
  const categoryPerms = allPermissions.filter(p => currentCategory?.perms.includes(p.code.toLowerCase()));

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[150] flex items-center justify-center p-4 animate-in fade-in" onClick={onClose}>
      <div className="bg-white rounded-[2.5rem] w-full max-w-4xl h-[85vh] flex flex-col shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gray-50 px-8 py-6 border-b border-gray-100 flex items-center justify-between">
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 bg-foret-500 rounded-3xl flex items-center justify-center text-white shadow-lg shadow-foret-100">
              <i className="ri-shield-keyhole-line text-3xl" />
            </div>
            <div>
              <h3 className="font-playfair font-black text-3xl text-gray-900 tracking-tight">Accès & Sécurité</h3>
              <p className="text-gray-400 font-medium mt-1">Personnalisation des droits pour <span className="text-foret-600 font-bold uppercase">{user.nom}</span></p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="w-12 h-12 flex items-center justify-center rounded-2xl hover:bg-gray-200 text-gray-400 transition-all cursor-pointer"
          >
            <i className="ri-close-line text-2xl" />
          </button>
        </div>

        <div className="flex-1 flex overflow-hidden">
          {/* Sidebar Tabs */}
          <div className="w-64 bg-gray-50/50 border-r border-gray-100 p-4 space-y-1 overflow-y-auto">
            {CATEGORIES.map(cat => (
              <button
                key={cat.id}
                onClick={() => setActiveTab(cat.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl text-sm font-bold transition-all cursor-pointer ${
                  activeTab === cat.id 
                    ? 'bg-white text-foret-600 shadow-sm border border-gray-100' 
                    : 'text-gray-500 hover:bg-gray-100'
                }`}
              >
                <i className={`${cat.icon} text-lg ${activeTab === cat.id ? 'text-foret-500' : 'opacity-40'}`} />
                <span>{cat.label}</span>
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 bg-white p-8 overflow-y-auto">
            {loading ? (
              <div className="h-full flex flex-col items-center justify-center opacity-30">
                <div className="w-10 h-10 border-4 border-foret-500 border-t-transparent rounded-full animate-spin mb-4" />
                <p className="font-bold">Chargement des droits...</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between mb-6">
                   <div className="flex flex-col">
                      <h4 className="font-black text-xl text-gray-900 uppercase tracking-tight">{currentCategory?.label}</h4>
                      <div className="flex gap-4 mt-2">
                         <button 
                            onClick={handleSelectAllCategory}
                            className="text-[10px] font-black text-foret-600 hover:text-foret-700 uppercase tracking-widest cursor-pointer"
                         >
                            Tout cocher
                         </button>
                         <button 
                            onClick={handleDeselectAllCategory}
                            className="text-[10px] font-black text-red-500 hover:text-red-600 uppercase tracking-widest cursor-pointer"
                         >
                            Tout décocher
                         </button>
                      </div>
                   </div>
                   <div className="text-[10px] font-black bg-gray-100 text-gray-400 px-3 py-1 rounded-full uppercase tracking-widest">
                     {categoryPerms.filter(p => userPerms.includes(p.code)).length} / {categoryPerms.length} actifs
                   </div>
                </div>

                <div className="grid grid-cols-1 gap-3">
                  {categoryPerms.length === 0 ? (
                    <p className="text-center py-20 text-gray-300 italic text-sm">Aucune permission spécifique dans cette catégorie.</p>
                  ) : (
                    categoryPerms.map(p => (
                      <div 
                        key={p.code}
                        onClick={() => handleToggle(p.code)}
                        className={`flex items-center justify-between p-5 rounded-3xl border-2 transition-all cursor-pointer group ${
                          userPerms.includes(p.code.toLowerCase())
                            ? 'border-foret-500 bg-foret-50/30'
                            : 'border-gray-50 hover:border-gray-100'
                        }`}
                      >
                        <div className="flex-1">
                          <p className={`font-black text-sm uppercase tracking-tight ${userPerms.includes(p.code.toLowerCase()) ? 'text-foret-900' : 'text-gray-900'}`}>
                            {p.nom}
                          </p>
                          <p className="text-xs text-gray-400 font-medium mt-1 leading-relaxed">
                            {p.description}
                          </p>
                        </div>
                        
                        <div className={`w-12 h-6 rounded-full relative transition-all duration-300 ${
                          userPerms.includes(p.code.toLowerCase()) ? 'bg-foret-500' : 'bg-gray-200'
                        }`}>
                          <div className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-all duration-300 shadow-sm ${
                            userPerms.includes(p.code.toLowerCase()) ? 'left-7' : 'left-1'
                          }`} />
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-8 py-6 border-t border-gray-100 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2 text-xs text-gray-400 font-bold">
            <i className="ri-information-line text-lg text-foret-500" />
            <span>Les changements seront effectifs à la prochaine connexion de l'utilisateur.</span>
          </div>
          <div className="flex gap-3">
            <button 
              onClick={onClose}
              className="px-8 py-4 text-gray-500 font-black text-sm hover:text-gray-700 cursor-pointer transition-all"
            >
              ANNULER
            </button>
            <button 
              onClick={handleSave}
              disabled={saving}
              className="px-10 py-4 bg-gray-900 text-white rounded-2xl text-sm font-black hover:bg-black transition-all cursor-pointer shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 active:scale-95"
            >
              {saving ? <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : <i className="ri-save-line" />}
              ENREGISTRER LES MODIFICATIONS
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
